import { TriangleDownIcon } from '@primer/octicons-react';
import { Box, Button } from '@primer/react';
import { ModelEntityType } from '@upright/common/model/input/types';
import { NetScoreFormat } from '@upright/common/model/output/scores/types';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { Text } from '@upright/ui/common/generic/components/primer-custom/text';
import { Radio, RadioGroup } from '@upright/ui/common/generic/components/radio';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { AggregateMetricProps, scoreTypeTitle } from '@upright/ui/common/upright/model/output/scores/aggregate-metrics';
import {
  NirFormulaWithDarkText,
  NisFormulaWithDarkText,
} from '@upright/ui/common/upright/model/output/scores/aggregate-metrics/styles';
import { useRef, useState } from 'react';
import styled from 'styled-components';

type Props = AggregateMetricProps & {
  entityType: ModelEntityType;
};
export function AggregateMetricSelector(props: Props) {
  return (
    <Root>
      <Top>
        <Text sx={{ color: 'fg.muted', pl: '9px' }} fontSize={'micro'}>
          Aggregate metric
        </Text>
      </Top>
      <FormatSelector {...props} />
    </Root>
  );
}

const FormatSelector = (props: Props) => {
  const { availableMetrics, currentMetric, setAggregateMetric } = props;

  const pushGtmEvent = usePushGtmEvent();

  const [open, setOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const windowHeight = window.innerHeight;

  const buttonBottom = buttonRef.current?.getBoundingClientRect().bottom;

  // OH: A hardcoded pixel value for the current explanation texts --> adjust as needed
  const dropDownHeight = 220;
  const enoughSpaceToShowDropDownBelowButton = buttonBottom ? windowHeight - buttonBottom > dropDownHeight : false;

  const onSelectFormat = (format: NetScoreFormat) => {
    if (setAggregateMetric) {
      setAggregateMetric(format);
    }
    setOpen(false);
    pushGtmEvent({
      event: CustomGtmEvent.IMPACT_PROFILE_NI_TREE_NET_SCORE_FORMAT_SELECT,
      data: {
        selectedFormat: format,
      },
    });
  };

  if (!availableMetrics || !setAggregateMetric) return <></>;

  return (
    <Box sx={{ mt: '-2px' }}>
      <Button
        ref={buttonRef}
        variant="invisible"
        size="small"
        onClick={() => {
          setOpen(!open);
        }}
        trailingAction={TriangleDownIcon}
      >
        {scoreTypeTitle(props.currentMetric)}
      </Button>

      <StyledDropDown showDropDown={open} showBelowButton={enoughSpaceToShowDropDownBelowButton}>
        <RadioGroup
          name="aggregateMetricRadioGroup"
          value={currentMetric}
          onChange={value => {
            onSelectFormat(value as NetScoreFormat);
          }}
          aria-label="Aggregate metric"
        >
          <Stack gap="md">
            {availableMetrics.map(o => (
              <Radio key={o} value={o} alignment="top">
                <Stack gap="xs">
                  <RadioLabelTitle>{scoreTypeTitle(o)}</RadioLabelTitle>
                  <RadioLabelCaption>{getMetricExplanation(o)}</RadioLabelCaption>
                </Stack>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </StyledDropDown>
    </Box>
  );
};

const getMetricExplanation = (metric: NetScoreFormat) => {
  if (metric === NetScoreFormat.NET_RATIO || metric === NetScoreFormat.NET_RATIO_DECIMAL) {
    return (
      <div>
        Ratio of all positive and negative impacts. Defined as{' '}
        <NirFormulaWithDarkText positiveText="positive impacts" negativeText="negative impacts" />. Possible theoretical
        range for net impact ratio is {`]`}-∞, 100%{`]`}.
      </div>
    );
  } else {
    return (
      <p>
        Net sum of all positive and negative impacts in the selected unit of measure. Defined as{' '}
        <NisFormulaWithDarkText positiveText="positive impacts" negativeText="negative impacts" />
      </p>
    );
  }
};

// OH: Copied from Primer's ActionMenu styling. Positioning of the ActionMenu -component breaks down when scrolling.
// Hence, handling the positioning logic ourselves while using the styling from Primer.
const StyledDropDown = styled.div<{ showDropDown: boolean; showBelowButton: boolean }>`
  display: ${props => (props.showDropDown ? 'block' : 'none')};
  box-shadow:
    rgba(31, 35, 40, 0.12) 0px 1px 3px,
    rgba(66, 74, 83, 0.12) 0px 8px 24px;
  position: absolute;
  ${props => (props.showBelowButton ? 'top: 24px' : 'bottom: 9px')};
  left: 8px;
  width: 256px;
  height: auto;
  border-radius: 12px;
  overflow: hidden;
  animation: 200ms cubic-bezier(0.33, 1, 0.68, 1) 0s 1 normal none running overlay-appear;
  visibility: var(--styled-overlay-visibility);
  padding: 16px 16px 8px;
  z-index: 1;
  background: white;
`;

const Top = styled.div`
  font-size: 87.5%;
  margin-top: 0.2em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 13em;
  padding-bottom: 2px;
`;

const RadioLabelTitle = styled.span`
  font-weight: var(--font-weight__bold);
  font-size: var(--font-size__md);
  white-space: nowrap;
`;

const RadioLabelCaption = styled.div`
  color: var(--fgColor-muted);
  font-size: var(--font-size__sm);
`;

const Root = styled.div``;
